import {
  AITaskResponseResultData,
  useRunAiTasksMutation,
  useUploadPredesignMutation,
} from 'api/collaborateApi';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import {
  AiSettingsFormData,
  AiSettingsProjectType,
  ViewportParms,
} from 'types/api/collaborate';
import { getBrowserInfo, isInChromeExtension } from 'utils/proxyUtils';
import { ScreenCapture } from '../ScreenCapture';
import { screenshotStorage } from '../services/screenshotStorage';
import { setAiScreenshotFile, setAiTaskData } from '../store/siteInfoSlice';
import { setAIPreviewImg } from '../store/tasksSlice';
import { setFullPageScreenshotId } from '../store/uiSlice';
import useViewportAnalyzer, {
  AtariMatesAnalysis,
  convertToAtariMatesFormat,
} from './use-analyze-view-port';
import { EnhancedElementProperties } from './use-element-analyzer';
import useGetPageId from './use-get-pageid';
import { usePageScroller } from './use-page-scroller';

interface UseAiPageReviewReturn {
  // State
  loading: boolean;
  screenshotFile: File | null;
  aiTaskData: unknown;
  isUploading: boolean;
  isRunningAiTask: boolean;
  isProcessing: boolean;

  // Actions
  // handleReviewPage: (
  //   reviewedIssues: AITaskResponseResultData[] | undefined
  // ) => Promise<void>;
  handleScrollingReview: (
    reviewedIssues?: AITaskResponseResultData[],
    options?: ScrollingReviewOptions,
    aiFormData?: AiSettingsFormData,
    currentPageId?: string
  ) => Promise<void>;

  // Utilities (exposed for flexibility)
  captureScreenshot: () => Promise<File | null>;
  uploadScreenshot: (file: File, uploadUrl: string) => Promise<Response>;
  uploadJson: (
    data: AtariMatesAnalysis | EnhancedElementProperties | string,
    uploadUrl: string
  ) => Promise<Response>;
}

interface ScrollingReviewOptions {
  scrollDelay?: number;
  overlapPixels?: number;
  uploadAllScreenshots?: boolean;
}

export const useAiPageReview = (): UseAiPageReviewReturn => {
  const { pageId } = useGetPageId();
  const dispatch = useDispatch();
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  // const [scrollProgress, setScrollProgress] = useState(0);
  const [_capturedScreenshots, setCapturedScreenshots] = useState<File[]>([]);
  const [_accumulatedResults, setAccumulatedResults] = useState<
    AITaskResponseResultData[]
  >([]);

  const [uploadPredesign, { isLoading: isUploading }] =
    useUploadPredesignMutation();
  const [
    runAiTask,
    { data: aiTaskData, isLoading: isRunningAiTask, isSuccess },
  ] = useRunAiTasksMutation();
  const isProcessing = isUploading || isRunningAiTask;
  const { analyzeViewport } = useViewportAnalyzer();
  const {
    scrollThroughPage,
    restoreOriginalPosition,
    // isScrolling,
    // scrollProgress,
  } = usePageScroller();
  // const [analysis, setAnalysis] = useState<ViewportAnalysis | null>(null);
  // const [atariData, setAtariData] = useState<AtariMatesAnalysis | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = useCallback(async () => {
    try {
      const viewportAnalysis = analyzeViewport();
      return convertToAtariMatesFormat(viewportAnalysis);
    } catch (error) {
      console.error('Analysis failed:', error);
      throw error;
    }
  }, [analyzeViewport]);

  useEffect(() => {
    if (isSuccess && aiTaskData) {
      dispatch(setAiTaskData(aiTaskData));
    }
  }, [aiTaskData, dispatch, isSuccess]);

  // Convert data URI to blob
  const dataURItoBlob = useCallback((dataURI: string): Blob => {
    const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const binary = atob(dataURI.split(',')[1]);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }

    return new Blob([array], { type: mime });
  }, []);

  // Capture screenshot and convert to File
  const captureScreenshot = useCallback(async (): Promise<File | null> => {
    try {
      const sc = new ScreenCapture();

      const data = await sc.capture({
        useChromeApi: isInChromeExtension(),
        quality: 40,
      });

      if (!data) {
        console.error('Screenshot data is null or undefined');
        return null;
      }

      const blob = dataURItoBlob(data);
      const file = new File([blob], 'screenshot.png', { type: blob.type });

      setScreenshotFile(file);
      const imageUrl = URL.createObjectURL(blob);
      dispatch(setAIPreviewImg(imageUrl || ''));
      return file;
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      return null;
    }
  }, [dataURItoBlob, dispatch]);

  // Upload file to presigned URL
  const uploadScreenshot = useCallback(
    async (file: File, uploadUrl: string): Promise<Response> => {
      const contentType = file.type || 'image/png';

      return fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });
    },
    []
  );

  // Upload JSON data to presigned URL
  const uploadJson = useCallback(
    async (
      data: AtariMatesAnalysis | EnhancedElementProperties | string,
      uploadUrl: string
    ): Promise<Response> => {
      const jsonBlob = new Blob(
        [typeof data === 'string' ? data : JSON.stringify(data, null, 2)],
        {
          type: typeof data === 'string' ? 'text/plain' : 'application/json',
        }
      );

      return fetch(uploadUrl, {
        method: 'PUT',
        body: jsonBlob,
        headers: {
          'Content-Type':
            typeof data === 'string' ? 'text/plain' : 'application/json',
        },
      });
    },
    []
  );

  // function to immediately dispatch new AI task result
  const dispatchNewAiTaskResult = useCallback(
    (newResult: AITaskResponseResultData[]) => {
      setAccumulatedResults((prev) => {
        const updatedResults = [...prev, ...newResult];

        dispatch(setAiTaskData(updatedResults));
        return updatedResults;
      });
    },
    [dispatch]
  );

  // Main review handler
  // const handleReviewPage = useCallback(
  //   async (reviewedIssues?: AITaskResponseResultData[]): Promise<void> => {
  //     try {
  //       if (reviewedIssues && reviewedIssues.length > 0) {
  //         toast.info(
  //           'You have already reviewed this page, please check the AI task list.'
  //         );
  //         dispatch(setAiTaskData(reviewedIssues));
  //         return;
  //       }
  //       setLoading(true);
  //       setAccumulatedResults([]);
  //       const screenshot = await captureScreenshot();
  //       setScreenshotFile(screenshot);

  //       const atariMatesData = await handleAnalyze();

  //       if (!screenshot || !atariMatesData) {
  //         toast.error(
  //           !screenshot
  //             ? 'Failed to capture screenshot'
  //             : 'Failed to analyze viewport'
  //         );
  //         return;
  //       }

  //       const uploadResponse = await uploadPredesign({
  //         files: [{ name: 'screenshot.png' }],
  //       }).unwrap();

  //       const uploadJsonResponse = await uploadPredesign({
  //         files: [{ name: 'ataridata.json' }],
  //       }).unwrap();

  //       const uploadResult = await uploadScreenshot(
  //         screenshot,
  //         uploadResponse.result[0].url
  //       );
  //       if (uploadResult.status !== 200 && uploadResult.status !== 201) {
  //         toast.error(
  //           `Screenshot upload failed with status: ${uploadResult.status}`
  //         );
  //       }

  //       const uploadJsonResult = await uploadJson(
  //         atariMatesData,
  //         uploadJsonResponse.result[0].url
  //       );
  //       if (
  //         uploadJsonResult.status !== 200 &&
  //         uploadJsonResult.status !== 201
  //       ) {
  //         toast.error(
  //           `JSON upload failed with status: ${uploadJsonResult.status}`
  //         );
  //         return;
  //       }

  //       if (!pageId) {
  //         toast.error('Page ID is required for AI analysis');
  //         return;
  //       }

  //       dispatch(setAiScreenshotFile(uploadResponse.result[0]));

  //       await runAiTask({
  //         pageId: pageId || '',
  //         params: {
  //           screenshot: uploadResponse.result[0].key,
  //           json: uploadJsonResponse.result[0].key,
  //         },
  //       }).unwrap();

  //       setLoading(false);
  //     } catch (error) {
  //       setLoading(false);
  //       toast.error('Error in review process');
  //       console.error('Error in review process:', error);
  //       dispatch(setAIPreviewImg(''));
  //     }
  //   },
  //   [
  //     captureScreenshot,
  //     handleAnalyze,
  //     uploadPredesign,
  //     uploadScreenshot,
  //     uploadJson,
  //     runAiTask,
  //     pageId,
  //     dispatch,
  //   ]
  // );

  const handleScrollingReview = useCallback(
    async (
      _reviewedIssues?: AITaskResponseResultData[],
      options: ScrollingReviewOptions = {},
      aiFormData?: AiSettingsFormData,
      currentPageId?: string
    ): Promise<void> => {
      const { scrollDelay = 1000, overlapPixels = 50 } = options;
      const isChromeExtension = isInChromeExtension();

      try {
        // Initialize state
        setLoading(true);
        setCapturedScreenshots([]);
        setAccumulatedResults([]);

        // STEP 1: Scroll through page and capture screenshots (handled by pageScroller)
        const positions = await scrollThroughPage({
          scrollDelay,
          overlapPixels,
          captureScreenshots: isChromeExtension, // Enable screenshot capture only in Chrome extension
          screenshotCapture: captureScreenshot, // Pass our screenshot function
          viewportAnalyzer: handleAnalyze,
          onScrollProgress: (
            _progress: number,
            _current: number,
            _total: number
          ) => {
            // console.log(
            //   `📊 Scroll progress: ${Math.round(progress * 100)}% (${current}/${total}px)`
            // )
          },
        });

        await restoreOriginalPosition();

        // Variables to track results
        const allScreenshots: File[] = [];
        let successCount: number = 0;
        const allViewportsData: ViewportParms[] = [];

        // STEP 2: Process each position in a loop
        for (let i = 0; i < positions.length; i++) {
          const position = positions[i];
          const isLastViewport = i === positions.length - 1;

          try {
            // Check for viewport analysis (required for all cases)
            if (!position.viewportAnalysis) {
              console.warn(
                `⚠️ No viewport analysis found for position ${i + 1}, skipping...`
              );
              continue;
            }

            // Check for screenshot only if in Chrome extension
            if (isChromeExtension && !position.screenshot) {
              console.warn(
                `⚠️ No screenshot found for position ${i + 1}, skipping...`
              );
              continue;
            }

            // Scroll back to this position for analysis (viewport analysis needs current view)
            await new Promise((resolve) => setTimeout(resolve, 300));

            // COMPLETE handleReviewPage WORKFLOW FOR THIS POSITION ===

            // 1. Analyze current viewport
            const atariMatesData = position.viewportAnalysis;

            // Store screenshot in our results only if it exists
            if (position.screenshot) {
              allScreenshots.push(position.screenshot);
            }

            // 2. Upload assets for this position
            let uploadResponse = null;
            let uploadJsonResponse = null;

            // Only upload screenshot if we have one (Chrome extension case)
            if (position.screenshot) {
              uploadResponse = await uploadPredesign({
                files: [{ name: position.screenshot.name }],
              }).unwrap();
            }

            uploadJsonResponse = await uploadPredesign({
              files: [{ name: `analysis-position-${i + 1}.json` }],
            }).unwrap();

            // Upload screenshot only if we have one
            if (position.screenshot && uploadResponse) {
              const uploadResult: Response = await uploadScreenshot(
                position.screenshot,
                uploadResponse.result[0].url
              );

              if (uploadResult.status !== 200 && uploadResult.status !== 201) {
                console.error(
                  `Screenshot upload failed for position ${i + 1}: ${uploadResult.status}`
                );
                continue;
              }
            }

            // Upload analysis JSON
            const uploadJsonResult: Response = await uploadJson(
              atariMatesData,
              uploadJsonResponse.result[0].url
            );

            if (
              uploadJsonResult.status !== 200 &&
              uploadJsonResult.status !== 201
            ) {
              console.error(
                `Analysis upload failed for position ${i + 1}: ${uploadJsonResult.status}`
              );
              continue;
            }

            const viewportData = {
              // Only include screenshot key if we uploaded one
              ...(uploadResponse && {
                screenshot: uploadResponse.result[0].key,
              }),
              json: uploadJsonResponse.result[0].key,
              width: atariMatesData?.page_metadata?.viewport.width,
              height: atariMatesData?.page_metadata?.viewport.height,
              scroll_to: position.scroll_to || 0,
            };

            allViewportsData.push(viewportData);
            successCount++;
            if (isLastViewport) {
              if (!pageId && !currentPageId) {
                console.error('Page ID is required for AI analysis');
                continue;
              }
              if (allViewportsData.length === 0) {
                console.error(
                  '❌ No valid viewport data collected for AI task'
                );
                toast.error('No valid viewports found to analyze');
                return;
              }

              // Show completion summary
              const totalCount = positions.length;
              if (successCount === 0) {
                toast.error('Failed to process any scroll positions');
              } else if (successCount === totalCount) {
                toast.success(
                  `Page review completed on ${successCount} viewports.`
                );
              } else {
                toast.warning(
                  `Page review completed on ${successCount}/${totalCount} viewports.`
                );
              }
              // 3. Trigger AI analysis for all viewports
              try {
                // Stitch together the existing viewport screenshots to create a full page screenshot
                let fullPageScreenshotKey: string | undefined;

                if (allScreenshots.length > 0 && isChromeExtension) {
                  try {
                    // Check if the final viewport should be excluded due to excessive overlap
                    let screenshotsToStitch = [...allScreenshots];
                    let positionsForStitching = [...positions];

                    if (allScreenshots.length > 1) {
                      const lastIndex = allScreenshots.length - 1;
                      const lastPosition = positions[lastIndex];

                      const currentScrollY = lastPosition.scroll_to || 0;
                      const remainingHeight =
                        lastPosition.documentHeight - currentScrollY;
                      const viewportHeight = lastPosition.viewportHeight;

                      // Skip final viewport if it contains less than 20% new content
                      const newContentRatio = remainingHeight / viewportHeight;
                      if (newContentRatio < 0.2) {
                        console.log(
                          `⚠️ Skipping final viewport: only ${Math.round(newContentRatio * 100)}% new content (${remainingHeight}px of ${viewportHeight}px viewport)`
                        );
                        screenshotsToStitch = allScreenshots.slice(0, -1);
                        positionsForStitching = positions.slice(0, -1);
                      }
                    }

                    console.log(
                      `🖼️ Starting to stitch ${screenshotsToStitch.length} viewport screenshots into full page screenshot`
                    );

                    // Create a canvas to stitch the screenshots together
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (ctx) {
                      // Calculate total height and max width from all screenshots
                      let totalHeight = 0;
                      let maxWidth = 0;

                      // First pass: calculate dimensions by cropping intersections
                      for (let i = 0; i < screenshotsToStitch.length; i++) {
                        const screenshot = screenshotsToStitch[i];
                        const position = positionsForStitching[i];
                        const img = new Image();
                        const imgUrl = URL.createObjectURL(screenshot);

                        await new Promise<void>((resolve, reject) => {
                          img.onload = () => {
                            if (i === 0) {
                              // First screenshot: use full height
                              totalHeight = img.height;
                            } else {
                              // Calculate overlap based on actual scroll positions
                              const currentScrollY = position.scroll_to || 0;
                              const previousScrollY =
                                positionsForStitching[i - 1].scroll_to || 0;
                              const actualScrollDistance =
                                currentScrollY - previousScrollY;

                              // If this is the final viewport and it has significant overlap, crop more aggressively
                              const isLastViewport =
                                i === screenshotsToStitch.length - 1;
                              let cropFromTop = 0;

                              if (isLastViewport) {
                                // For final viewport, check if we have excessive overlap
                                const remainingHeight =
                                  position.documentHeight - currentScrollY;
                                const viewportHeight = position.viewportHeight;

                                if (remainingHeight < viewportHeight * 0.3) {
                                  // If less than 30% of viewport height remains, crop significantly
                                  cropFromTop = Math.max(
                                    0,
                                    img.height - remainingHeight
                                  );
                                } else {
                                  // Normal overlap handling
                                  cropFromTop = Math.max(
                                    0,
                                    img.height - actualScrollDistance
                                  );
                                }
                              } else {
                                // Normal overlap calculation for non-final viewports
                                cropFromTop = Math.max(
                                  0,
                                  img.height - actualScrollDistance
                                );
                              }

                              const newContentHeight = Math.max(
                                0,
                                img.height - cropFromTop
                              );
                              totalHeight += newContentHeight;

                              console.log(
                                `📏 Screenshot ${i + 1}: scroll distance=${actualScrollDistance}px, crop=${cropFromTop}px, new content=${newContentHeight}px`
                              );
                            }

                            maxWidth = Math.max(maxWidth, img.width);
                            URL.revokeObjectURL(imgUrl);
                            resolve();
                          };
                          img.onerror = () => {
                            URL.revokeObjectURL(imgUrl);
                            reject(new Error('Failed to load image'));
                          };
                          img.src = imgUrl;
                        });
                      }

                      console.log(
                        `📏 Calculated dimensions: ${maxWidth}px × ${totalHeight}px (cropping intersections)`
                      );

                      if (canvas) {
                        // Generate a unique ID for the screenshot
                        const screenshotId = `fullPage_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

                        const dataUrl = canvas.toDataURL('image/png', 0.8);
                        // Store the screenshot in IndexedDB
                        await screenshotStorage.storeScreenshot(
                          screenshotId,
                          dataUrl,
                          'fullPage'
                        );

                        // Store only the ID in Redux
                        dispatch(setFullPageScreenshotId(screenshotId));

                        console.log(
                          'stitched full page screenshot id',
                          screenshotId
                        );
                        console.log(
                          'stitched full page screenshot url',
                          dataUrl
                        );
                      }

                      // Set canvas dimensions
                      canvas.width = maxWidth;
                      canvas.height = totalHeight;

                      // Check if height exceeds 4000px and scale down if needed
                      let finalWidth = maxWidth;
                      let finalHeight = totalHeight;
                      let scaleFactor = 1;

                      if (totalHeight > 4000) {
                        scaleFactor = 4000 / totalHeight;
                        finalWidth = Math.round(maxWidth * scaleFactor);
                        finalHeight = 4000;

                        console.log(
                          `🔍 Height exceeds 4000px, scaling down by factor: ${scaleFactor.toFixed(3)}`
                        );
                        console.log(
                          `📐 Final scaled dimensions: ${finalWidth}px × ${finalHeight}px`
                        );

                        // Create a new scaled canvas
                        const scaledCanvas = document.createElement('canvas');
                        const scaledCtx = scaledCanvas.getContext('2d');
                        scaledCanvas.width = finalWidth;
                        scaledCanvas.height = finalHeight;

                        // Scale the context
                        scaledCtx!.scale(scaleFactor, scaleFactor);

                        // Second pass: stitch images together on the scaled canvas with cropping
                        let currentY = 0;

                        for (let i = 0; i < screenshotsToStitch.length; i++) {
                          const screenshot = screenshotsToStitch[i];
                          const position = positionsForStitching[i];
                          const img = new Image();
                          const imgUrl = URL.createObjectURL(screenshot);

                          await new Promise<void>((resolve, reject) => {
                            img.onload = () => {
                              if (i === 0) {
                                // First screenshot: draw at the top
                                scaledCtx!.drawImage(img, 0, 0);
                                currentY = img.height;
                              } else {
                                // Calculate overlap based on actual scroll positions
                                const currentScrollY = position.scroll_to || 0;
                                const previousScrollY =
                                  positionsForStitching[i - 1].scroll_to || 0;
                                const actualScrollDistance =
                                  currentScrollY - previousScrollY;

                                // If this is the final viewport and it has significant overlap, crop more aggressively
                                const isLastViewport =
                                  i === screenshotsToStitch.length - 1;
                                let cropFromTop = 0;

                                if (isLastViewport) {
                                  // For final viewport, check if we have excessive overlap
                                  const remainingHeight =
                                    position.documentHeight - currentScrollY;
                                  const viewportHeight =
                                    position.viewportHeight;

                                  if (remainingHeight < viewportHeight * 0.3) {
                                    // If less than 30% of viewport height remains, crop significantly
                                    cropFromTop = Math.max(
                                      0,
                                      img.height - remainingHeight
                                    );
                                  } else {
                                    // Normal overlap handling
                                    cropFromTop = Math.max(
                                      0,
                                      img.height - actualScrollDistance
                                    );
                                  }
                                } else {
                                  // Normal overlap calculation for non-final viewports
                                  cropFromTop = Math.max(
                                    0,
                                    img.height - actualScrollDistance
                                  );
                                }

                                const newContentHeight = Math.max(
                                  0,
                                  img.height - cropFromTop
                                );

                                if (newContentHeight > 0) {
                                  // Draw only the new content portion (crop out intersection)
                                  scaledCtx!.drawImage(
                                    img,
                                    0,
                                    cropFromTop,
                                    img.width,
                                    newContentHeight, // Source: crop from cropFromTop
                                    0,
                                    currentY,
                                    img.width,
                                    newContentHeight // Destination: place after previous content
                                  );
                                  currentY += newContentHeight;
                                }
                              }
                              URL.revokeObjectURL(imgUrl);
                              resolve();
                            };
                            img.onerror = () => {
                              URL.revokeObjectURL(imgUrl);
                              reject(new Error('Failed to load image'));
                            };
                            img.src = imgUrl;
                          });
                        }

                        // Use the scaled canvas for the final blob
                        const blob = await new Promise<Blob>((resolve) => {
                          scaledCanvas.toBlob(
                            (blob) => {
                              resolve(blob!);
                            },
                            'image/png',
                            0.8
                          );
                        });

                        console.log(
                          `✅ Scaled full page screenshot created: ${(blob.size / 1024).toFixed(1)}KB`
                        );

                        const fullPageScreenshotFile = new File(
                          [blob],
                          'stitched-full-page-screenshot.png',
                          { type: 'image/png' }
                        );

                        // Upload the stitched full page screenshot
                        const fullPageUploadResponse = await uploadPredesign({
                          files: [{ name: fullPageScreenshotFile.name }],
                        }).unwrap();

                        const fullPageUploadResult = await uploadScreenshot(
                          fullPageScreenshotFile,
                          fullPageUploadResponse.result[0].url
                        );

                        if (
                          fullPageUploadResult.status === 200 ||
                          fullPageUploadResult.status === 201
                        ) {
                          fullPageScreenshotKey =
                            fullPageUploadResponse.result[0].key;
                          console.log(
                            `📤 Scaled full page screenshot uploaded successfully with key: ${fullPageScreenshotKey}`
                          );
                        }
                      } else {
                        console.log(
                          `✅ Height is under 4000px, using original dimensions`
                        );

                        // Original stitching logic for screenshots under 4000px height with cropping
                        let currentY = 0;

                        for (let i = 0; i < screenshotsToStitch.length; i++) {
                          const screenshot = screenshotsToStitch[i];
                          const position = positionsForStitching[i];
                          const img = new Image();
                          const imgUrl = URL.createObjectURL(screenshot);

                          await new Promise<void>((resolve, reject) => {
                            img.onload = () => {
                              if (i === 0) {
                                // First screenshot: draw at the top
                                ctx.drawImage(img, 0, 0);
                                currentY = img.height;
                              } else {
                                // Calculate overlap based on actual scroll positions
                                const currentScrollY = position.scroll_to || 0;
                                const previousScrollY =
                                  positionsForStitching[i - 1].scroll_to || 0;
                                const actualScrollDistance =
                                  currentScrollY - previousScrollY;

                                // If this is the final viewport and it has significant overlap, crop more aggressively
                                const isLastViewport =
                                  i === screenshotsToStitch.length - 1;
                                let cropFromTop = 0;

                                if (isLastViewport) {
                                  // For final viewport, check if we have excessive overlap
                                  const remainingHeight =
                                    position.documentHeight - currentScrollY;
                                  const viewportHeight =
                                    position.viewportHeight;

                                  if (remainingHeight < viewportHeight * 0.3) {
                                    // If less than 30% of viewport height remains, crop significantly
                                    cropFromTop = Math.max(
                                      0,
                                      img.height - remainingHeight
                                    );
                                  } else {
                                    // Normal overlap handling
                                    cropFromTop = Math.max(
                                      0,
                                      img.height - actualScrollDistance
                                    );
                                  }
                                } else {
                                  // Normal overlap calculation for non-final viewports
                                  cropFromTop = Math.max(
                                    0,
                                    img.height - actualScrollDistance
                                  );
                                }

                                const newContentHeight = Math.max(
                                  0,
                                  img.height - cropFromTop
                                );

                                if (newContentHeight > 0) {
                                  // Draw only the new content portion (crop out intersection)
                                  ctx.drawImage(
                                    img,
                                    0,
                                    cropFromTop,
                                    img.width,
                                    newContentHeight, // Source: crop from cropFromTop
                                    0,
                                    currentY,
                                    img.width,
                                    newContentHeight // Destination: place after previous content
                                  );
                                  currentY += newContentHeight;
                                }
                              }
                              URL.revokeObjectURL(imgUrl);
                              resolve();
                            };
                            img.onerror = () => {
                              URL.revokeObjectURL(imgUrl);
                              reject(new Error('Failed to load image'));
                            };
                            img.src = imgUrl;
                          });
                        }

                        // Convert canvas to blob and then to File
                        const blob = await new Promise<Blob>((resolve) => {
                          canvas.toBlob(
                            (blob) => {
                              resolve(blob!);
                            },
                            'image/png',
                            0.8
                          );
                        });

                        console.log(
                          `✅ Full page screenshot created: ${(blob.size / 1024).toFixed(1)}KB`
                        );

                        const fullPageScreenshotFile = new File(
                          [blob],
                          'stitched-full-page-screenshot.png',
                          { type: 'image/png' }
                        );

                        // Upload the stitched full page screenshot
                        const fullPageUploadResponse = await uploadPredesign({
                          files: [{ name: fullPageScreenshotFile.name }],
                        }).unwrap();

                        const fullPageUploadResult = await uploadScreenshot(
                          fullPageScreenshotFile,
                          fullPageUploadResponse.result[0].url
                        );

                        if (
                          fullPageUploadResult.status === 200 ||
                          fullPageUploadResult.status === 201
                        ) {
                          fullPageScreenshotKey =
                            fullPageUploadResponse.result[0].key;
                          console.log(
                            `📤 Full page screenshot uploaded successfully with key: ${fullPageScreenshotKey}`
                          );
                        }
                      }
                    }
                  } catch (stitchError) {
                    console.warn(
                      'Failed to stitch viewport screenshots:',
                      stitchError
                    );
                    // Continue without full page screenshot if stitching fails
                  }
                }

                const browserInfo = await getBrowserInfo();
                const aiTaskResult = await runAiTask({
                  pageId: pageId || currentPageId || '',
                  params: allViewportsData,
                  settings: {
                    brand_name: aiFormData?.brandName || '',
                    guidelines: aiFormData?.customGuidelines || '',
                    enabled_experts: aiFormData?.enabledBots || [],
                    project_types:
                      aiFormData?.projectTypes.reduce(
                        (acc: string[], curr: AiSettingsProjectType) => {
                          acc.push(curr.label);
                          return acc;
                        },
                        []
                      ) || [],
                    audience: aiFormData?.targetAudience || '',
                    voice_tone: aiFormData?.voiceTone || '',
                    page_height: position.documentHeight,
                    review_intent: aiFormData?.reviewIntent || '',
                  },
                  browser_version: `${browserInfo.name} ${browserInfo.version}`,
                  full_page_screenshot: fullPageScreenshotKey,
                }).unwrap();

                // Track AI task
                if (
                  aiTaskResult &&
                  Array.isArray(aiTaskResult) &&
                  aiTaskResult.length > 0
                ) {
                  dispatchNewAiTaskResult(aiTaskResult);
                }
              } catch (aiError) {
                console.error('❌ AI task failed:', aiError);
                // toast.error(`AI task failed for position ${i + 1}`);
              }
            }

            // Update state with first screenshot (for UI display) only if we have one
            if (i === 0 && uploadResponse) {
              dispatch(setAiScreenshotFile(uploadResponse.result[0]));
            }
          } catch (positionError) {
            console.error(
              `❌ Error processing position ${i + 1}:`,
              positionError
            );
            // toast.error(`Failed to process scroll position ${i + 1}`);
          }
        }

        // STEP 3: Restore original scroll position
        await restoreOriginalPosition();

        // Update final state
        setCapturedScreenshots(allScreenshots);
      } catch (error) {
        console.error('❌ Loop-based scrolling review failed:', error);
        // toast.error('Error in scrolling review process');

        // Try to restore original position even if there was an error
        try {
          await restoreOriginalPosition();
        } catch (restoreError) {
          console.error(
            'Failed to restore original scroll position:',
            restoreError
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [
      scrollThroughPage,
      restoreOriginalPosition,
      captureScreenshot,
      handleAnalyze,
      uploadPredesign,
      uploadScreenshot,
      uploadJson,
      runAiTask,
      pageId,
      dispatch,
      dispatchNewAiTaskResult,
    ]
  );

  return {
    // State
    loading,
    screenshotFile,
    aiTaskData,
    isUploading,
    isRunningAiTask,
    isProcessing,
    // Actions
    // handleReviewPage,
    handleScrollingReview,

    // Utilities
    captureScreenshot,
    uploadScreenshot,
    uploadJson,
  };
};
